import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { Badge } from "../models/badges";
import BadgesService from "../services/badges.service";
import { imageUrlToDataUrl } from "../components/ComponentsSticker/TrickStickerPicker/embedImage";
import { PinStickerInput } from "../components/ComponentsSticker/TrickStickerPicker/pinStickers";
import { getAdjustedTiming, QueryTiming } from "./useQueryConfig";

// Resolve a user's earned pins (badge IDs from profile.badges) into render-ready
// medallion inputs. Two steps run inside the query so a consumer just gets back
// data it can hand straight to buildPinStickers():
//   1. badgeId -> Badge (BadgesService.getBadgeByID), same as Profile.tsx
//   2. Badge.link -> embedded data: URL (so the sticker rasterizes without
//      tainting the canvas). A failed embed yields dataUrl: null, which the
//      medallion renders as a monogram fallback.
//
// Generous cap so virtually every rider sees ALL their pins as stickers, while
// still bounding the image-fetch storm for an extreme power-user. The query is
// gated by `enabled` so nothing runs until the editor actually opens.
const MAX_PINS = 40;

export interface UseUserPinsResult {
    pins: PinStickerInput[];
    isLoading: boolean;
}

const useUserPins = (badgeIds: string[] | undefined, enabled: boolean): UseUserPinsResult => {
    const ids = useMemo(() => (badgeIds ?? []).slice(0, MAX_PINS), [badgeIds]);

    const { data, isLoading } = useQuery({
        queryKey: ["stickerUserPins", ids.join(",")],
        queryFn: async (): Promise<PinStickerInput[]> => {
            const badges: (Badge | null)[] = await Promise.all(
                ids.map((id) => BadgesService.getBadgeByID(id)),
            );

            const valid = badges.filter(
                (b): b is Badge => !!b && (!!b.name || !!b.link),
            );

            return Promise.all(
                valid.map(async (b) => ({
                    id: b.id,
                    name: b.name || "Pin",
                    dataUrl: b.link ? await imageUrlToDataUrl(b.link) : null,
                })),
            );
        },
        enabled: enabled && ids.length > 0,
        ...getAdjustedTiming(QueryTiming.LOW_FREQUENCY),
    });

    return { pins: data ?? [], isLoading: enabled && ids.length > 0 && isLoading };
};

export default useUserPins;
